import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActiveDialog, LayoutModule } from '@monsido/angular-shared-components';
import { TranslateModule } from 'app/modules/translate.module';
import { BackendAccessibilityChecksRepo } from '../../../services/api/support/admin/backend-accessibility-checks.repo';
import { Papa } from 'ngx-papaparse';
import { AccessibilityCheckModel } from '../../../services/api/support/models/accessibility-check-model';

interface ExportProgress {
    current: number;
    total: number;
    message: string;
}

interface ExportField {
    key: string;
    label: string;
    selected: boolean;
    required: boolean;
}

type WizardStep = 'configure' | 'progress';

@Component({
    selector: 'mon-export-accessibility-checks',
    templateUrl: './export-accessibility-checks.component.html',
    styleUrls: ['./export-accessibility-checks.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, LayoutModule, TranslateModule],
})
export class ExportAccessibilityChecksComponent implements OnInit, OnDestroy {
    @Input() backendAccessibilityChecksRepo: BackendAccessibilityChecksRepo;

    currentStep: WizardStep = 'configure';
    progress: ExportProgress = { current: 0, total: 0, message: '' };
    isExporting = false;
    isCompleted = false;
    cancelRequested = false;
    errorMessage = '';
    exportedCount = 0;
    elapsedTime = 0;
    checkIdFrom: number | null = null;
    checkIdTo: number | null = null;

    exportFields: ExportField[] = [
        { key: 'id', label: 'Database ID', selected: true, required: true },
        { key: 'check_id', label: 'Check ID', selected: true, required: true },
        { key: 'name', label: 'Name', selected: true, required: false },
        { key: 'description', label: 'Description', selected: true, required: false },
        { key: 'friendly_name', label: 'Friendly Name', selected: true, required: false },
        { key: 'impact', label: 'Impact', selected: true, required: false },
        { key: 'difficulty', label: 'Difficulty', selected: true, required: false },
        { key: 'article_url', label: 'Article URL', selected: true, required: false },
    ];

    private startTime: number;
    private timerInterval: number;

    constructor (
        private activeDialog: ActiveDialog,
        private papa: Papa,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit (): void {

    }

    ngOnDestroy (): void {
        this.stopTimer();
    }

    get hasSelectedFields (): boolean {
        return this.exportFields.some(field => field.selected);
    }

    get hasValidCheckIdRange (): boolean {
        if (this.checkIdFrom === null && this.checkIdTo === null) {
            return true;
        }
        if (this.checkIdFrom !== null && this.checkIdTo !== null) {
            return this.checkIdTo >= this.checkIdFrom;
        }
        return true;
    }

    get canStartExport (): boolean {
        return this.hasSelectedFields && this.hasValidCheckIdRange;
    }

    startExportProcess (): void {
        this.currentStep = 'progress';
        this.isExporting = true;
        this.startTime = Date.now();
        this.startTimer();
        this.cdRef.markForCheck();
        this.startExport();
    }

    private startTimer (): void {
        this.timerInterval = window.setInterval(() => {
            this.elapsedTime = Math.floor((Date.now() - this.startTime) / 1000);
            this.cdRef.markForCheck();
        }, 1000);
    }

    private stopTimer (): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    }

    async startExport (): Promise<void> {
        try {
            this.progress.message = 'Fetching accessibility checks...';
            this.cdRef.markForCheck();

            const pageSize = 50;
            const allChecks: AccessibilityCheckModel[] = [];

            const firstPageResponse = await this.backendAccessibilityChecksRepo.getAll({
                page: 1,
                page_size: pageSize,
                search: '',
            });

            const total = (firstPageResponse as AccessibilityCheckModel[] & { total?: number }).total || firstPageResponse.length;
            this.progress.total = total;
            this.progress.current = firstPageResponse.length;
            allChecks.push(...firstPageResponse);
            this.cdRef.markForCheck();

            if (this.cancelRequested) {
                this.handleCancellation();
                return;
            }

            const totalPages = Math.ceil(total / pageSize);

            for (let page = 2; page <= totalPages; page++) {
                if (this.cancelRequested) {
                    this.handleCancellation();
                    return;
                }

                this.progress.message = `Fetching page ${page} of ${totalPages}...`;
                this.cdRef.markForCheck();

                const pageResponse = await this.backendAccessibilityChecksRepo.getAll({
                    page,
                    page_size: pageSize,
                    search: '',
                });

                allChecks.push(...pageResponse);
                this.progress.current = allChecks.length;
                this.cdRef.markForCheck();
            }

            if (this.cancelRequested) {
                this.handleCancellation();
                return;
            }

            let filteredChecks = allChecks;
            if (this.checkIdFrom !== null || this.checkIdTo !== null) {
                filteredChecks = allChecks.filter(check => {
                    const checkId = check.check_id;
                    if (this.checkIdFrom !== null && checkId < this.checkIdFrom) {
                        return false;
                    }
                    if (this.checkIdTo !== null && checkId > this.checkIdTo) {
                        return false;
                    }
                    return true;
                });
            }

            this.progress.total = filteredChecks.length;
            this.progress.current = filteredChecks.length;
            this.cdRef.markForCheck();

            filteredChecks.sort((a, b) => a.id - b.id);

            this.progress.message = 'Preparing CSV file...';
            this.cdRef.markForCheck();

            const selectedFields = this.exportFields.filter(f => f.selected);
            const csvData = filteredChecks.map(check => {
                const row: Record<string, unknown> = {};
                selectedFields.forEach(field => {
                    let value: unknown;
                    if (field.key === 'impact') {
                        value = this.formatImpact(check.impact);
                    } else if (field.key === 'difficulty') {
                        value = this.formatDifficulty(check.difficulty);
                    } else {
                        value = check[field.key as keyof AccessibilityCheckModel];
                    }
                    // Use field label as the CSV column header
                    row[field.label] = value;
                });
                return row;
            });

            const csv = this.papa.unparse(csvData, {
                quotes: true,
                header: true,
            });

            this.downloadCSV(csv, this.generateFilename());

            this.exportedCount = filteredChecks.length;
            this.isCompleted = true;
            this.isExporting = false;
            this.progress.message = `Successfully exported ${this.exportedCount} checks in ${this.formatTime(this.elapsedTime)}`;
            this.stopTimer();
            this.cdRef.markForCheck();

        } catch (error) {
            this.handleError(error);
        }
    }

    private handleCancellation (): void {
        this.isExporting = false;
        this.progress.message = 'Export cancelled';
        this.stopTimer();
        this.cdRef.markForCheck();
        setTimeout(() => {
            this.activeDialog.close(false);
        }, 500);
    }

    private handleError (error: unknown): void {
        this.isExporting = false;
        this.isCompleted = false;
        const errorMessage = error instanceof Error ? error.message : 'An error occurred during export';
        this.errorMessage = errorMessage;
        this.stopTimer();
        this.cdRef.markForCheck();
    }

    cancel (): void {
        this.cancelRequested = true;
        this.progress.message = 'Cancelling export...';
        this.cdRef.markForCheck();
    }

    cancelDialog (): void {
        this.activeDialog.close(false);
    }

    close (): void {
        this.activeDialog.close(true);
    }

    getProgressPercentage (): number {
        if (!this.progress.total || this.progress.total === 0) {
            return 0;
        }
        return Math.min(100, (this.progress.current / this.progress.total) * 100);
    }

    getProgressBarColor (): string {
        const percentage = this.getProgressPercentage();
        if (percentage < 30) {
            return 'bg-info';
        } else if (percentage < 70) {
            return 'bg-primary';
        } else if (percentage < 100) {
            return 'bg-warning';
        } else {
            return 'bg-success';
        }
    }

    getStatusMessage (): string {
        if (this.isCompleted) {
            return this.progress.message;
        }
        return this.progress.message || 'Processing...';
    }

    formatTime (seconds: number): string {
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    }

    private generateFilename (): string {
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
        return `accessibility_checks_${timestamp}.csv`;
    }

    private downloadCSV (csvContent: string, filename: string): void {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    }

    private formatImpact (impact: string): string {
        const impactMap: Record<string, string> = {
            'minor': 'Minor',
            'moderate': 'Moderate',
            'serious': 'Serious',
        };
        return impactMap[impact?.toLowerCase()] || impact;
    }

    private formatDifficulty (difficulty: number): string {
        if (difficulty >= 1 && difficulty <= 10) {
            return `${difficulty} - Level ${difficulty}`;
        }
        return String(difficulty);
    }
}
